// §7.5 — the version-retention rule.
//
// It lives here for the same reason the §7.1 path grammar does: **two stores must agree.** §7.5
// applies to the OPFS history a guest keeps and to the `file_versions` table a signed-in user's
// server keeps, and a rule implemented twice is a rule that will one day thin two histories
// differently — with the user's own file as the thing that disagrees.
//
// The function is pure: a list, a clock, a policy, and the exact set that survives. The *policy*
// stays with each package's `config.ts` (§22), because the numbers are configuration — the server
// reads `HANGAR_VERSION_KEEP_COUNT` and `HANGAR_VERSION_KEEP_DAYS` from §3 — and only the rule is
// shared.
import type { VersionEntry } from './vfs';

const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

export interface RetentionPolicy {
  /** Never keep fewer than this many versions, however old they are. */
  keepCount: number;
  /** Never thin away a version younger than this many days. */
  keepDays: number;
  /** Every version younger than this many hours survives. */
  keepAllHours: number;
  /** Up to this many days old, one per hour survives; beyond it, one per day. */
  hourlyDays: number;
}

export interface ThinResult {
  /** Newest first. */
  keep: VersionEntry[];
  drop: VersionEntry[];
}

/**
 * §7.5's retention rule.
 *
 * Keep **all** versions from the last 24 h, **hourly** for the last 7 days, **daily** beyond —
 * and never fewer than `keepCount` (50) or younger than `keepDays` (30), *whichever is larger*.
 *
 * Both floors are prefixes of the newest-first order, so one always contains the other and
 * "whichever is larger" is just the bigger of the two counts. Buckets are epoch-aligned (UTC),
 * and the survivor of a bucket is its **newest** member — the state at the end of that hour or
 * day, which is what someone scrubbing back through a document is looking for.
 */
export function thinVersions(
  entries: readonly VersionEntry[],
  now: number,
  policy: RetentionPolicy,
): ThinResult {
  const ordered = [...entries].sort((a, b) => b.createdAt - a.createdAt);
  const keepDaysMs = policy.keepDays * MS_PER_DAY;
  const keepAllMs = policy.keepAllHours * MS_PER_HOUR;
  const hourlyMs = policy.hourlyDays * MS_PER_DAY;

  const withinKeepDays = ordered.filter((entry) => now - entry.createdAt < keepDaysMs).length;
  const floor = Math.max(policy.keepCount, withinKeepDays);

  const keep: VersionEntry[] = [];
  const drop: VersionEntry[] = [];
  const claimed = new Set<string>();

  ordered.forEach((entry, index) => {
    const age = now - entry.createdAt;
    const bucket =
      age < hourlyMs
        ? `h${Math.floor(entry.createdAt / MS_PER_HOUR)}`
        : `d${Math.floor(entry.createdAt / MS_PER_DAY)}`;

    // Inside either floor, or inside the keep-everything window: survives unconditionally, and
    // claims its bucket so an older sibling in the same hour is not kept as well.
    if (index < floor || age < keepAllMs) {
      keep.push(entry);
      claimed.add(bucket);
      return;
    }
    if (claimed.has(bucket)) {
      drop.push(entry);
      return;
    }
    claimed.add(bucket);
    keep.push(entry);
  });

  return { keep, drop };
}
